import React, { useEffect } from "react";
import {
  Box,
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
  FormControl,
  Typography,
  Radio,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Alert,
  Chip,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import type { AppDispatch, RootState } from "@store/store";
import type { RootReducerType } from "@store/store";
import {
  getAmenitiesUtility,
  getAuthorityListThunk,
  proposedUtilityAlignmentThunk,
  submitAmenitiesROWThunk,
  getRowUtilitiesThunk,
} from "../redux/thunk/amenitiesThunk";
import {
  changeAmmenityUtilityType,
  setAmenityROWField,
  setAmenityROWFields,
  setIsFirstSectionCompletedRow,
} from "../redux/amenitesROWSlice";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { setActiveStep } from "../redux/stepperROWSlice";
import { StepsButton } from "./StepsButtons";
import { toast } from "react-toastify";
import {
  StyledFormControl,
  StyledTextField,
} from "@components/CustomInput/customInput";
import { useNavigate } from "react-router-dom";
import MapSearchInput from "@gis/components/MapSearchInput";

import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import Loader from "@modules/stepper/components/Loader";
import ChainageSection from "./Chainage/ChainageSection";
import { Chainage, Crossing } from "./Chainage/types";

// Define proper TypeScript interfaces
interface FieldOption {
  label: string;
  value: string | number;
}

interface FormField {
  type: string;
  label: string;
  name: string;
  options?: string[] | FieldOption[];
  optional?: boolean;
  inputType?: string;
  readOnly?: boolean;
}

interface Section {
  title: string;
  fields: FormField[];
}

// Define sections for ROW component
const initialSections: Section[] = [
  {
    title: "Utility Details",
    fields: [
      {
        type: "radio",
        label: "Type of Utility",
        name: "utility_type",
        options: [],
      },
      {
        type: "select",
        label: "Utility Category",
        name: "amenity",
        options: [],
      },
      {
        type: "input",
        label: "Specify Utility Type",
        name: "other_utility_type",
        optional: true,
      },
      {
        type: "select",
        label: "Proposed Utility Alignment",
        name: "proposed_utility_alignment",
        options: [],
      },
    ],
  },
  {
    title: "Chainage & Coordinates",
    fields: [
      // Chainage fields will be handled separately in renderChainageSection
    ],
  },
  {
    title: "Location Details",
    fields: [
      {
        type: "input",
        label: "State / Union Territories",
        name: "state",
      },
      {
        type: "input",
        label: "District",
        name: "district",
      },
      {
        type: "input",
        label: "Pincode",
        name: "pincode",
        inputType: "number",
      },
      {
        type: "select",
        label: "Name of Authority to which National Highway is entrusted",
        name: "authority_id",
        options: [],
      },
    ],
  },
  {
    title: "Highway Details",
    fields: [
      {
        type: "select",
        label: "Highway Category",
        name: "highway_category",
        options: ["2-Lane", "4-Lane", "6-Lane", "8-Lane", "IL / SL"],
      },
      {
        type: "select",
        label: "Highway Side",
        name: "highway_side",
        options: [
          "Left-Hand Side",
          "Right-Hand Side",
          "Both Side of the Highway",
        ],
      },
      {
        type: "input",
        label: "National Highway Number",
        name: "national_highway_number",
      },
      {
        type: "input",
        label: "Taluka",
        name: "taluka",
      },
      {
        type: "input",
        label:
          "Number of Villages through which the proposed utility is passing:",
        name: "villages_crossed_count",
        inputType: "number",
      },
      {
        type: "input",
        label:
          "Names of villages through which the proposed utility is passing:",
        name: "villages_crossed_names",
      },
      {
        type: "input",
        label:
          "Number of Tehsils through which the proposed utility is passing:",
        name: "tehsils_crossed_count",
        inputType: "number",
      },
      {
        type: "input",
        label: "Name of Tehsils through which the proposed utility is passing:",
        name: "tehsils_crossed_names",
      },
    ],
  },
];

export const AmenitiesSectionROW: React.FC<any> = ({
  collapsed,
  onChainageChange,
}) => {
  const {
    district,
    // khasra_number,
    national_highway_number,
    taluka,
    village,
    villages_crossed_count,
    villages_crossed_names,
    tehsils_crossed_count,
    tehsils_crossed_names,
    pincode,
    state,
    state_code,
    highway_side,
    utility_type,
    category_id,
    highway_category,
    amenity,
    other_utility_type,
    // gst_number,
    start_chainage_km,
    start_chainage_m,
    end_chainage_km,
    end_chainage_m,
    start_latitude,
    start_longitude,
    end_latitude,
    end_longitude,
    // circle_rate,
    // plot_area,
    // plot_length_m,
    // plot_shape,
    // plot_width_m,
    authority_name,
    authority_id,
    proposed_utility_alignment,
    ro_id,
    piu_id,
    piu_ids,
    application_id,
    chainage_Details,
    crossing_Details,
  } = useSelector((state: RootReducerType) => state.amenitiesROW);
  const navigate = useNavigate();

  // const { access_id } = useSelector(
  //   (state: RootState) => state.amenitiesROW || {}
  // );
  const { activeStep } = useSelector(
    (state: RootReducerType) => state.stepperROW,
  );
  const { start_chainage, end_chainage } = useSelector(
    (state: RootReducerType) => state.applicationDashboard,
  );

  const dispatch = useDispatch<AppDispatch>();

  const [showBharatNetModal, setShowBharatNetModal] = React.useState(false);
  const [bharatNetFile, setBharatNetFile] = React.useState<File | null>(null);
  const [bharatNetConsent, setBharatNetConsent] = React.useState(false);
  const [bharatNetAcknowledged, setBharatNetAcknowledged] =
    React.useState(false);
  const [sections, setSections] = React.useState<Section[]>(initialSections);
  const [expandedSection, setExpandedSection] = React.useState<number>(0);
  const [completedSections, setCompletedSections] = React.useState<number[]>(
    [],
  );
  const [chainageDetails, setChainageDetails] = React.useState<Chainage[]>([]);
  const [crossingDetails, setCrossingDetails] = React.useState<Crossing[]>([]);
  const [showLocationSearch, setShowLocationSearch] =
    React.useState<boolean>(false);
  const [showMainSections, setShowMainSections] =
    React.useState<boolean>(false);
  const [hasSearchedLocation, setHasSearchedLocation] =
    React.useState<boolean>(false);
  const [isLoading, setisLoading] = React.useState(false);

  const [bharatnetRes, setBharatnetRes] = React.useState<{
    fileName?: string;
    objectKey?: string;
    originalFileName?: string;
  }>({});

  React.useEffect(() => {
    if ((utility_type as any) === "BharatNet Project") {
      setShowBharatNetModal(true);
    } else {
      // Reset acknowledgement if user switches away from BharatNet
      setBharatNetAcknowledged(false);
      setBharatNetFile(null);
      setBharatNetConsent(false);
    }
  }, [utility_type]);

  const handleBharatNetCancel = () => {
    setShowBharatNetModal(false);
    setBharatNetConsent(false);
    setBharatNetFile(null);
    setBharatNetAcknowledged(false);
    // Revert utility_type selection since user cancelled the undertaking
    dispatch(changeAmmenityUtilityType(""));
    dispatch(setAmenityROWField({ name: "category_id", value: "" }));
  };

  const handleBharatNetAcknowledge = async () => {
    if (!bharatNetFile) {
      toast.error("Please upload the supporting document to proceed.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("undertaking_document", bharatNetFile);
      // TODO: Add API call here once the endpoint is ready
      // Example:
      // const response = await dispatch(submitBharatNetUndertakingThunk(formData));
      // if (response.payload.success) {
      //   toast.success(response.payload.message);
      //   setShowBharatNetModal(false);
      // } else {
      //   toast.error(response.payload.message || "Failed to submit undertaking");
      // }

      setShowBharatNetModal(false);
      setBharatNetAcknowledged(true);
    } catch (error) {
      console.error("BharatNet acknowledgement error:", error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  // Memoize initial values to prevent re-creation on every render
  const formikInitialValues = React.useMemo(
    () => ({
      district: district ?? "",
      highway_category: highway_category ?? "",
      // khasra_number: khasra_number ?? "",
      national_highway_number: national_highway_number ?? "",
      taluka: taluka ?? "",
      village: village ?? "",
      villages_crossed_count: villages_crossed_count ?? null,
      tehsils_crossed_count: tehsils_crossed_count ?? null,
      villages_crossed_names: Array.isArray(villages_crossed_names)
        ? villages_crossed_names
        : [],
      tehsils_crossed_names: Array.isArray(tehsils_crossed_names)
        ? tehsils_crossed_names
        : [],
      pincode: pincode ?? "",
      start_latitude: start_latitude ?? "",
      start_longitude: start_longitude ?? "",
      end_latitude: end_latitude ?? "",
      end_longitude: end_longitude ?? "",
      state: state ?? "",
      highway_side: highway_side ?? "",
      utility_type: utility_type ?? "",
      proposed_utility_alignment: proposed_utility_alignment ?? "",
      state_code: state_code ?? null,
      amenity: amenity ?? "",
      other_utility_type: other_utility_type ?? "",
      // gst_number: gst_number ?? "",
      start_chainage_km:
        start_chainage?.split("+")[0] || start_chainage_km || "",
      start_chainage_m: start_chainage?.split("+")[1] || start_chainage_m || "",
      end_chainage_km: end_chainage?.split("+")[0] || end_chainage_km || "",
      end_chainage_m: end_chainage?.split("+")[1] || end_chainage_m || "",
      // circle_rate: circle_rate ?? null,
      // plot_area: plot_area ?? "",
      // plot_length_m: plot_length_m ?? "",
      // plot_width_m: plot_width_m ?? "",
      authority_id: authority_id ?? "",
      // ro_id: 7,
      // piu_id: 82,
      // piu_ids: [82],
      application_id,
      ro_id: Number(ro_id),
      piu_id: Number(piu_id),
      piu_ids: [Number(piu_id)],
    }),
    [
      district,
      highway_category,
      // khasra_number,
      national_highway_number,
      villages_crossed_count,
      villages_crossed_names,
      tehsils_crossed_count,
      tehsils_crossed_names,
      pincode,
      start_latitude,
      start_longitude,
      end_latitude,
      end_longitude,
      state,
      highway_side,
      utility_type,
      proposed_utility_alignment,
      state_code,
      amenity,
      // gst_number,
      start_chainage,
      end_chainage,
      // circle_rate,
      // plot_area,
      // plot_length_m,
      // plot_width_m,
      authority_id,
      application_id,
    ],
  );

  // Validation helper functions
  const validateInput = {
    // Only alphabets and spaces
    alphabetsOnly: (value: string) => /^[a-zA-Z ]*$/.test(value),

    // Only numbers
    numbersOnly: (value: string) => /^[0-9]*$/.test(value),

    // Numbers and forward slash for khasra number
    khasraNumber: (value: string) => /^[0-9/]*$/.test(value),

    // Alphanumeric with hyphen for highway number
    highwayNumber: (value: string) => /^[a-zA-Z0-9-]*$/.test(value),

    // Address fields: alphanumeric with specific special characters (. , / - #)
    address: (value: string) => /^[a-zA-Z0-9 .,/-]*$/.test(value),

    // Taluka: alphanumeric with spaces
    taluka: (value: string) => /^[a-zA-Z0-9]*$/.test(value),

    // Numeric fields (chainage) - prevent negative
    positiveNumber: (value: string) => !value.startsWith("-"),
  };

  const sanitizeTextOnly = (values: string[]) =>
    values.map((v) => v.replace(/[0-9]/g, "").trim());

  const formikValidationSchema = Yup.object({
    utility_type: Yup.string().required("Type of Utility is required"),
    proposed_utility_alignment: Yup.string().required(
      "Proposed Utility Alignment is required",
    ),
    amenity: Yup.string().required("Amenity is required"),
    other_utility_type: Yup.string().when("amenity", {
      is: (value: string) => value?.toLowerCase() === "other utilities",
      then: (schema) => schema.required("Please specify the utility type"),
      otherwise: (schema) => schema.notRequired(),
    }),
    district: Yup.string()
      .matches(/^[a-zA-Z ]+$/, "Only alphabets and spaces are allowed")
      .required("District is required"),
    state: Yup.string()
      .matches(/^[a-zA-Z ]+$/, "Only alphabets and spaces are allowed")
      .required("State is required"),
    pincode: Yup.string()
      .transform((value, originalValue) => String(originalValue || "").trim())
      .matches(/^\d{6}$/, "Pincode must be a valid 6-digit number")
      .required("Pincode is required"),
    authority_id: Yup.string().required("Authority Name is required"),
    highway_side: Yup.string().required("Side of Highway is required"),
    start_latitude: Yup.string().required("Start Latitude is required"),
    start_longitude: Yup.string().required("Start Longitude is required"),
    end_latitude: Yup.string().required("End Latitude is required"),
    end_longitude: Yup.string().required("End Longitude is required"),
    // khasra_number: Yup.string().required("Khasra Number is required"),
    national_highway_number: Yup.string()
      .matches(
        /^[a-zA-Z0-9-]+$/,
        "Only letters, numbers and hyphen (-) are allowed",
      )
      .required("National Highway Number is required"),
    taluka: Yup.string()
      .matches(
        /^[a-zA-Z0-9 ]+$/,
        "Only letters, numbers and spaces are allowed",
      )
      .required("Taluka is required"),
    villages_crossed_count: Yup.number()
      .required("Villages Crossed Count is required")
      .min(1, "Count must be at least 1")
      .max(50, "Count cannot exceed 50")
      .integer("Count must be a whole number"),
    tehsils_crossed_count: Yup.number()
      .required("Tehsils Crossed Count is required")
      .min(1, "Count must be at least 1")
      .max(50, "Count cannot exceed 50")
      .integer("Count must be a whole number"),
    villages_crossed_names: Yup.array()
      .of(
        Yup.string()
          .matches(/^[A-Za-z\s]+$/, "Only alphabets and spaces are allowed")
          .required("Village name is required"),
      )
      .min(1, "Enter at least one village name")
      .test(
        "count-match",
        "Number of village names must match the villages count",
        function (value) {
          const count = this.parent.villages_crossed_count;
          if (count && value) {
            return value.length === Number(count);
          }
          return true;
        },
      ),
    tehsils_crossed_names: Yup.array()
      .of(
        Yup.string()
          .matches(/^[A-Za-z\s]+$/, "Only alphabets and spaces are allowed")
          .required("Tehsil name is required"),
      )
      .min(1, "Enter at least one tehsil name")
      .test(
        "count-match",
        "Number of tehsil names must match the tehsils count",
        function (value) {
          const count = this.parent.tehsils_crossed_count;
          if (count && value) {
            return value.length === Number(count);
          }
          return true;
        },
      ),
    highway_category: Yup.string().required("Please Select Highway Category"),
    // start_chainage_km: Yup.number()
    //   .typeError("Start Chainage Km must be a number")
    //   .required("Start Chainage Km is required")
    //   .min(0, "Chainage Km must be >= 0"),
    // start_chainage_m: Yup.number()
    //   .typeError("Start Chainage m must be a number")
    //   .required("Start Chainage m is required")
    //   .min(0, "Start Chainage m must be >= 0")
    //   .max(9999, "Start Chainage m must be <= 9999"),
    // end_chainage_km: Yup.number()
    //   .typeError("End Chainage Km must be a number")
    //   .required("End Chainage Km is required")
    //   .min(0, "End Chainage Km must be >= 0"),
    // end_chainage_m: Yup.number()
    //   .typeError("End Chainage m must be a number")
    //   .required("End Chainage m is required")
    //   .min(0, "End Chainage m must be >= 0")
    //   .max(9999, "End Chainage m must be <= 9999"),
    // circle_rate: Yup.number().required("Circle Rate is required")
  });

  const formik = useFormik({
    initialValues: formikInitialValues,
    validationSchema: formikValidationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      // if (values.ro_id === 0) {
      //   toast.error(
      //     "Selected location at map in not under any juridiction of RO/PIU. Please select proper location.",
      //   );
      //   return;
      // }
      const {
        highway_category,
        start_latitude,
        start_longitude,
        end_latitude,
        end_longitude,
        start_chainage_km,
        start_chainage_m,
        end_chainage_km,
        end_chainage_m,
        amenity,
        proposed_utility_alignment,
        // circle_rate,
        authority_id,
        ...rest
      } = values;

      const chainage_Details = chainageDetails.map((item, index) => ({
        id: index,
        start_chainage_km: Number(item.startChainageKm),
        start_chainage_m: Number(item.startChainageM),
        end_chainage_km: Number(item.endChainageKm),
        end_chainage_m: Number(item.endChainageM),
        side: item.side === "Left-Hand Side" ? "LHS" : "RHS",
        length_km: Number(item.chainageLength),
        cable_pipe_details: item.cableDetails,
        order: index + 1,
      }));

      const crossing_Details = crossingDetails.map((item, index) => ({
        id: index,
        crossing_chainage_km: Number(item.crossingChainageKm),
        crossing_chainage_m: Number(item.crossingChainageM),
        length_km: Number(item.chainageLength),
        cable_pipe_details: item.cableDetails,
        order: chainage_Details.length + index + 1,
      }));

      if (chainageDetails.length === 0 && crossingDetails.length === 0) {
        toast.error("Please add at least one record.");
        return;
      }

      let payloadStartChainageKm: number | undefined;
      let payloadStartChainageM: number | undefined;
      let payloadEndChainageKm: number | undefined;
      let payloadEndChainageM: number | undefined;

      if (selectedAlignment === "Along the Highway") {
        const first = chainageDetails[0];
        const last = chainageDetails[chainageDetails.length - 1];

        payloadStartChainageKm = Number(first?.startChainageKm);
        payloadStartChainageM = Number(first?.startChainageM);
        payloadEndChainageKm = Number(last?.endChainageKm);
        payloadEndChainageM = Number(last?.endChainageM);
      } else if (selectedAlignment === "Across the Highway") {
        const first = crossingDetails[0];

        payloadStartChainageKm = Number(first?.crossingChainageKm);
        payloadStartChainageM = Number(first?.crossingChainageM);
      } else if (selectedAlignment === "Along & Across the Highway") {
        // Prefer Along chainage for summary values
        const first = chainageDetails[0];
        const last = chainageDetails[chainageDetails.length - 1];

        if (first && last) {
          payloadStartChainageKm = Number(first.startChainageKm);
          payloadStartChainageM = Number(first.startChainageM);
          payloadEndChainageKm = Number(last.endChainageKm);
          payloadEndChainageM = Number(last.endChainageM);
        } else if (crossingDetails.length) {
          // Fallback if only crossing exists
          payloadStartChainageKm = Number(
            crossingDetails[0].crossingChainageKm,
          );
          payloadStartChainageM = Number(crossingDetails[0].crossingChainageM);
        }
      }

      const submittedValues = {
        ...rest,
        // ...(access_id !== 0 && { application_id: access_id }),
        ...(application_id && { access_id: application_id }),
        highway_category: highway_category,
        pincode: Number(values.pincode) || "",
        // khasra_number: values.khasra_number || "",
        // gst_number: gstNumber || "",
        form_step: 1,
        start_latitude: start_latitude || 0,
        start_longitude: start_longitude || 0,
        end_latitude: end_latitude || 0,
        end_longitude: end_longitude || 0,
        state_code: state_code || "",
        amenity: amenity || "",
        other_utility_type:
          other_utility_type || values.other_utility_type || "",
        proposed_utility_alignment: proposed_utility_alignment || "",
        start_chainage:
          payloadStartChainageKm != null && payloadStartChainageM != null
            ? `${payloadStartChainageKm}+${payloadStartChainageM}`
            : "",

        end_chainage:
          payloadEndChainageKm != null && payloadEndChainageM != null
            ? `${payloadEndChainageKm}+${payloadEndChainageM}`
            : "",
        start_chainage_km: payloadStartChainageKm ?? "",
        start_chainage_m: payloadStartChainageM ?? "",
        end_chainage_km: payloadEndChainageKm ?? "",
        end_chainage_m: payloadEndChainageM ?? "",
        // circle_rate: Number(circle_rate) ?? 0,
        authority_id: authority_id ?? "",
        villages_crossed_count: Number(villages_crossed_count) ?? null,
        tehsils_crossed_count: Number(tehsils_crossed_count) ?? null,
        villages_crossed_names: Array.isArray(values.villages_crossed_names)
          ? values.villages_crossed_names
          : [],
        tehsils_crossed_names: Array.isArray(values.tehsils_crossed_names)
          ? values.tehsils_crossed_names
          : [],
        bharatnet_document: {
          fileName: bharatnetRes?.fileName || "",
          objectKey: bharatnetRes?.objectKey || "",
          file_name: bharatnetRes?.originalFileName || "",
        },
        ...(selectedAlignment === "Along the Highway" && {
          chainage_Details,
        }),

        ...(selectedAlignment === "Across the Highway" && {
          crossing_Details,
        }),

        ...(selectedAlignment === "Along & Across the Highway" && {
          chainage_Details,
          crossing_Details,
        }),
      };
      // return;
      dispatch(setAmenityROWFields(submittedValues));
      const response = await dispatch(submitAmenitiesROWThunk(submittedValues));
      if (response.payload.success) {
        dispatch(
          setAmenityROWField({
            name: "access_id",
            value: response.payload.data.application_id,
          }),
        );
        toast.success(response?.payload?.message);
        dispatch(setActiveStep(activeStep + 1));
      } else {
        toast.error(response?.payload?.response?.data?.message || response?.payload?.message || "Failed to save application");
      }
    },
  });

  const handleFieldChange = (name: string, value: any) => {
    formik.setFieldValue(name, value);

    setTimeout(() => {
      dispatch(setAmenityROWField({ name, value }));
    }, 0);
  };

  const selectedAlignment = React.useMemo(() => {
    const option = sections[0]?.fields
      ?.find((f) => f.name === "proposed_utility_alignment")
      ?.options?.find(
        (item: any) => item.value === formik.values.proposed_utility_alignment,
      ) as any;

    return option?.label ?? "";
  }, [sections, formik.values.proposed_utility_alignment]);

  // const handleStandardChange = (
  //   e: React.ChangeEvent<any> | SelectChangeEvent<string>
  // ) => {
  //   const { name, value } = e.target as { name: string; value: string };

  //   // Pincode - only numbers (6 digits)
  //   if (name === "pincode" && value && !validateInput.numbersOnly(value)) {
  //     return;
  //   }

  //   // National highway number - alphanumeric with hyphen
  //   if (
  //     name === "national_highway_number" &&
  //     !validateInput.highwayNumber(value)
  //   ) {
  //     return;
  //   }

  //   // Taluka - alphanumeric with spaces
  //   if (name === "taluka" && !validateInput.taluka(value)) {
  //     return;
  //   }

  //   // Village, District, State - only alphabets and spaces
  //   if (
  //     ["village", "district", "state"].includes(name) &&
  //     !validateInput.alphabetsOnly(value)
  //   ) {
  //     return;
  //   }
  //   if (
  //     ["tehsils_crossed_count", "village_crossed_count", "state"].includes(
  //       name
  //     ) &&
  //     !validateInput.numbersOnly(value)
  //   ) {
  //     return;
  //   }

  //   formik.handleChange(e);

  //   setTimeout(() => {
  //     dispatch(setAmenityROWField({ name, value }));
  //   }, 0);
  // };

  // Get all fields for validation
  const allFields = React.useMemo(() => {
    return sections.flatMap((section) => section.fields);
  }, [sections]);

  // Helper function to check if a value is populated
  const isValuePopulated = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "number") return value !== 0;
    if (Array.isArray(value))
      return value.length > 0 && value.every((item) => item.trim() !== "");
    return true;
  };

  // Check if a section is completed
  // Check if a section is completed
  const isSectionCompleted = (sectionIndex: number): boolean => {
    const section = sections[sectionIndex];
    if (!section) return false;

    // Special handling for chainage section (now section 1)
    if (sectionIndex === 1) {
      let hasAlignmentData = false;

      switch (selectedAlignment) {
        case "Along the Highway":
          hasAlignmentData = chainageDetails.length > 0;
          break;

        case "Across the Highway":
          hasAlignmentData = crossingDetails.length > 0;
          break;

        case "Along & Across the Highway":
          hasAlignmentData =
            chainageDetails.length > 0 && crossingDetails.length > 0;
          break;

        default:
          hasAlignmentData = false;
      }
      return (
        // isValuePopulated(formik.values.start_chainage_km) &&
        // isValuePopulated(formik.values.start_chainage_m) &&
        // isValuePopulated(formik.values.end_chainage_km) &&
        // isValuePopulated(formik.values.end_chainage_m) &&
        hasAlignmentData &&
        isValuePopulated(formik.values.start_latitude || start_latitude) &&
        isValuePopulated(formik.values.start_longitude || start_longitude) &&
        isValuePopulated(formik.values.end_latitude || end_latitude) &&
        isValuePopulated(formik.values.end_longitude || end_longitude)
      );
    }

    return section.fields.every((field) => {
      if (field.optional) return true;

      if (field.name === "villages_crossed_names") {
        const count = Number(formik.values.villages_crossed_count) || 0;
        const names = formik.values.villages_crossed_names;
        return count > 0 && Array.isArray(names) && names.length === count;
      }

      if (field.name === "tehsils_crossed_names") {
        const count = Number(formik.values.tehsils_crossed_count) || 0;
        const names = formik.values.tehsils_crossed_names;
        return count > 0 && Array.isArray(names) && names.length === count;
      }

      return isValuePopulated(
        formik.values[field.name as keyof typeof formik.values],
      );
    });
  };

  // Check if a section is enabled (previous sections are completed)
  const isSectionEnabled = (sectionIndex: number): boolean => {
    if (sectionIndex === 0) return true;
    for (let i = 0; i < sectionIndex; i++) {
      if (!completedSections.includes(i)) {
        return false;
      }
    }
    return true;
  };

  // Update completed sections when form values change
  React.useEffect(() => {
    const newCompletedSections: number[] = [];
    sections.forEach((_, index) => {
      if (isSectionCompleted(index)) {
        newCompletedSections.push(index);
      }
    });
    setCompletedSections(newCompletedSections);

    // Update global state when first section completion changes
    const isFirstComplete = newCompletedSections.includes(0);
    dispatch(setIsFirstSectionCompletedRow(isFirstComplete));

    // Only auto-expand if the user hasn't manually expanded a section
    // AND we're not in the first section with location search not triggered
    // if (!completedSections.includes(expandedSection) &&
    //   !(expandedSection === 0 && !showLocationSearch && !showMainSections)) {
    //   const firstIncompleteSection = sections.findIndex((_, index) =>
    //     !newCompletedSections.includes(index)
    //   );
    //   if (firstIncompleteSection !== -1) {
    //     setExpandedSection(firstIncompleteSection);
    //   } else {
    //     setExpandedSection(sections.length - 1);
    //   }
    // }
  }, [formik.values, sections, crossingDetails, chainageDetails]);

  const fetchCategoryList = async () => {
    const categoryList = await dispatch(getAmenitiesUtility(category_id));
    if (categoryList.payload.success) {
      setSections((prevSections) =>
        prevSections.map((section) => ({
          ...section,
          fields: section.fields.map((field) =>
            field.name === "amenity"
              ? {
                  ...field,
                  options: categoryList.payload.data.map((cat: any) => ({
                    label: cat.amenity_name,
                    value: cat.amenity_name,
                  })),
                }
              : field,
          ),
        })),
      );
    }
  };

  const fetchAuthorityListList = async () => {
    try {
      const authorityList = await dispatch(getAuthorityListThunk());
      if (authorityList?.payload?.success) {
        setSections((prevSections) =>
          prevSections.map((section) => ({
            ...section,
            fields: section.fields.map((field) =>
              field.name === "authority_id"
                ? {
                    ...field,
                    options: authorityList?.payload?.data?.map(
                      (authority: any) => ({
                        label: authority.name,
                        value: authority.id,
                      }),
                    ),
                  }
                : field,
            ),
          })),
        );
        // Set default value if authority_id exists and hasn't been set yet
        if (authority_id && !formik.values.authority_id) {
          formik.setFieldValue("authority_id", authority_id);
        }
      }
    } catch (error) {
      console.error("Error fetching authority list:", error);
    }
  };

  const fetchProposedUtilityAlignmentOptions = async () => {
    const response = await dispatch(proposedUtilityAlignmentThunk());
    if (response.payload.success) {
      setSections((prevSections) =>
        prevSections.map((section) => ({
          ...section,
          fields: section.fields.map((field) =>
            field.name === "proposed_utility_alignment"
              ? {
                  ...field,
                  options: response?.payload?.data?.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                  })),
                }
              : field,
          ),
        })),
      );
    }
  };

  React.useEffect(() => {
    const fetchUtilityTypes = async () => {
      const response = await dispatch(getRowUtilitiesThunk());
      if (response.payload?.success) {
        setSections((prevSections) =>
          prevSections.map((section) => ({
            ...section,
            fields: section.fields.map((field) =>
              field.name === "utility_type"
                ? {
                    ...field,
                    options: response.payload.data.map((item: any) => ({
                      label: item.name,
                      value: item.id,
                    })),
                  }
                : field,
            ),
          })),
        );
      }
    };
    fetchUtilityTypes();
    fetchCategoryList();
    fetchProposedUtilityAlignmentOptions();
    fetchAuthorityListList();
  }, []);

  React.useEffect(() => {
    if (!utility_type && sections[0]?.fields[0]?.options?.length) {
      const firstOption = sections[0].fields[0].options[0] as FieldOption;
      dispatch(changeAmmenityUtilityType(firstOption.label));
      dispatch(
        setAmenityROWField({
          name: "category_id",
          value: firstOption.value,
        }),
      );
    }
  }, [sections]);

  useEffect(() => {
    if (!category_id) return;
    fetchCategoryList();
  }, [category_id, dispatch]);

  React.useEffect(() => {
    let startKm: number | undefined;
    let startM: number | undefined;
    let endKm: number | undefined;
    let endM: number | undefined;

    if (selectedAlignment === "Along the Highway") {
      const first = chainageDetails[0];
      const last = chainageDetails[chainageDetails.length - 1];

      startKm = Number(first?.startChainageKm);
      startM = Number(first?.startChainageM);

      endKm = Number(last?.endChainageKm);
      endM = Number(last?.endChainageM);
    } else if (selectedAlignment === "Across the Highway") {
      const first = crossingDetails[0];

      startKm = Number(first?.crossingChainageKm);
      startM = Number(first?.crossingChainageM);
    } else if (selectedAlignment === "Along & Across the Highway") {
      const first = chainageDetails[0];
      const last = chainageDetails[chainageDetails.length - 1];

      if (first && last) {
        startKm = Number(first.startChainageKm);
        startM = Number(first.startChainageM);

        endKm = Number(last.endChainageKm);
        endM = Number(last.endChainageM);
      } else if (crossingDetails.length) {
        startKm = Number(crossingDetails[0].crossingChainageKm);
        startM = Number(crossingDetails[0].crossingChainageM);
      }
    }

    const startChainage =
      startKm !== undefined &&
      startM !== undefined &&
      !Number.isNaN(startKm) &&
      !Number.isNaN(startM)
        ? `${startKm}+${startM}`
        : "";

    const endChainage =
      endKm !== undefined &&
      endM !== undefined &&
      !Number.isNaN(endKm) &&
      !Number.isNaN(endM)
        ? `${endKm}+${endM}`
        : "";

    onChainageChange?.({
      startChainage,
      endChainage,
    });
  }, [selectedAlignment, chainageDetails, crossingDetails, onChainageChange]);

  const handleChange = (
    e: React.ChangeEvent<any> | SelectChangeEvent<string>,
  ) => {
    formik.handleChange(e);
    const { name, value } = e.target as { name: string; value: string };
    setTimeout(() => {
      dispatch(setAmenityROWField({ name, value }));
    }, 0);
  };

  const handleAccordionChange =
    (sectionIndex: number) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded && isSectionEnabled(sectionIndex)) {
        setExpandedSection(sectionIndex);
      }
    };

  const showSelectedSections = () => {
    if (start_latitude || start_longitude || end_latitude || end_longitude) {
      setShowMainSections(true);
      setShowLocationSearch(false);
      setHasSearchedLocation(true);
      setExpandedSection(0);
    }
  };

  const handleBackToFirstSection = () => {
    setShowLocationSearch(false);
    setShowMainSections(false);
    setExpandedSection(0); // Keep first section expanded when going back
  };

  const handleSearchLocation = () => {
    setShowMainSections(true);
    setShowLocationSearch(false);
    setHasSearchedLocation(true);
    // Only expand the next section after search is completed
    const nextSection = 1; // Chainage & Coordinates section
    setExpandedSection(nextSection);
  };

  useEffect(() => {
    showSelectedSections();
  }, [start_latitude, start_longitude, end_latitude, end_longitude]);

  useEffect(() => {
    setChainageDetails(
      (chainage_Details || []).map((item: any) => ({
        id: item.id,

        startChainageKm: item.start_chainage_km,
        startChainageM: item.start_chainage_m,

        endChainageKm: item.end_chainage_km,
        endChainageM: item.end_chainage_m,

        side: item.side === "LHS" ? "Left-Hand Side" : "Right-Hand Side",

        chainageLength: item.length_km,

        cableDetails: item.cable_pipe_details,

        order: item.order_index,
      })),
    );

    setCrossingDetails(
      (crossing_Details || []).map((item: any) => ({
        id: item.id,

        crossingChainageKm: item.crossing_chainage_km,

        crossingChainageM: item.crossing_chainage_m,

        chainageLength: item.length_km,

        cableDetails: item.cable_pipe_details,

        order: item.order_index,
      })),
    );
  }, [chainage_Details, crossing_Details]);

  // for village and tehsil multi string input
  const MultiStringInput = ({
    value,
    onChange,
    label,
    name,
    error,
    helperText,
    maxItems,
    required = false,
    allowedPattern,
    disabled,
  }: {
    value: string[];
    onChange: (newValue: string[]) => void;
    label: string;
    name: string;
    error?: boolean;
    helperText?: string;
    maxItems?: number;
    required?: boolean;
    allowedPattern?: RegExp;
    disabled?: boolean;
  }) => {
    const [inputValue, setInputValue] = React.useState("");

    const isMaxReached = maxItems !== undefined && value.length >= maxItems;

    const addChip = (val: string) => {
      const trimmed = val.trim();
      if (!trimmed) return;
      if (isMaxReached) return;
      if (value.includes(trimmed)) return;
      if (allowedPattern && !allowedPattern.test(trimmed)) return;

      onChange([...value, trimmed]);
      setInputValue("");
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (isMaxReached) return;
        addChip(inputValue);
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData("text");
      if (!pastedText) return;
      e.preventDefault();

      const items = pastedText
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .filter((v) => (allowedPattern ? allowedPattern.test(v) : true));

      let newValues = [...value];

      for (const item of items) {
        if (maxItems && newValues.length >= maxItems) break;
        if (!newValues.includes(item)) {
          newValues.push(item);
        }
      }

      onChange(sanitizeTextOnly(newValues));
      setInputValue("");
    };

    const handleDelete = (chip: string) => {
      onChange(value.filter((v) => v !== chip));
    };

    return (
      <Box sx={{ width: "27rem", mb: 1 }}>
        {/* Label */}
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
          {required && <span style={{ color: "red" }}> *</span>}
        </Typography>

        {/* Input + chips container */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: error ? "error.main" : "#d0d5dd",
            borderRadius: "8px",
            padding: "8px",
            minHeight: 45,
            backgroundColor: "#fff",
          }}
        >
          {/* Chips */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {value.map((chip, idx) => (
              <Chip
                key={`${chip}-${idx}`}
                label={chip}
                onDelete={() => handleDelete(chip)}
                deleteIcon={<ClearIcon />}
                sx={{
                  backgroundColor: "#eee",
                  borderRadius: "6px",
                }}
              />
            ))}
          </Box>

          {/* Text input */}
          <input
            name={name}
            value={inputValue}
            onChange={(e) => {
              if (isMaxReached) return;

              setInputValue(e.target.value.replace(/[0-9]/g, ""));
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={isMaxReached || disabled}
            placeholder={
              isMaxReached
                ? `Maximum ${maxItems} items reached`
                : allowedPattern
                  ? "Enter value and press Enter"
                  : "Enter value and press Enter"
            }
            style={{
              border: "none",
              outline: "none",
              width: "100%",
              background: "transparent",
              marginTop: 6,
              fontSize: 14,
            }}
          />
        </Box>

        {/* Helper / error text */}
        {(error || helperText || maxItems) && (
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              color: error ? "error.main" : "text.secondary",
            }}
          >
            {error
              ? helperText
              : maxItems
                ? `${value.length}/${maxItems} items added`
                : helperText}
          </Typography>
        )}
      </Box>
    );
  };

  const renderRadioField = (field: FormField) => {
    const error =
      formik.touched[field.name as keyof typeof formik.touched] &&
      formik.errors[field.name as keyof typeof formik.errors];

    return (
      <FormControl component="fieldset" error={Boolean(error)}>
        <Box display="flex" flexDirection="column" gap={1} mb={0} mt={1}>
          {(field.options as FieldOption[]).map((option) => (
            <FormControlLabel
              sx={{ marginBottom: 0 }}
              key={String(option.value)}
              value={String(option.value)}
              control={
                <Radio
                  sx={{
                    color: "grey.600",
                    display: "flex",
                    alignItems: "center",
                    paddingBlock: 0,
                    "&.Mui-checked": {
                      color: "#3a1c73",
                    },
                  }}
                  checked={utility_type === option.label}
                  onChange={() => {
                    dispatch(
                      setAmenityROWField({
                        name: "category_id",
                        value: option.value,
                      }),
                    );
                    dispatch(changeAmmenityUtilityType(option.label));
                  }}
                />
              }
              label={<Typography>{option.label}</Typography>}
              name={field.name}
            />
          ))}
        </Box>
        {error && (
          <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
            {formik.errors[field.name as keyof typeof formik.errors] as string}
          </Typography>
        )}
      </FormControl>
    );
  };

  const renderSelectField = (field: FormField) => {
    const error =
      formik.touched[field.name as keyof typeof formik.touched] &&
      formik.errors[field.name as keyof typeof formik.errors];

    return (
      <StyledFormControl
        sx={{ width: "27rem" }}
        variant="outlined"
        fullWidth
        error={Boolean(error)}
      >
        <Select
          sx={{ mb: 1 }}
          id={field.name}
          name={field.name}
          value={String(
            formik.values[field.name as keyof typeof formik.values],
          )}
          onChange={handleChange}
          onBlur={formik.handleBlur}
          IconComponent={ExpandMoreIcon}
          // disabled={
          //   field.name === "authority_id" && Boolean(formik.values.authority_id)
          // }
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 200,
              },
            },
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
          }}
        >
          <MenuItem disabled value="">
            Select {field.label || field.name}
          </MenuItem>

          {field.options?.map((option: any) =>
            typeof option === "object" ? (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ) : (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ),
          )}
        </Select>

        {error && (
          <Typography color="error" variant="caption">
            {formik.errors[field.name as keyof typeof formik.errors] as string}
          </Typography>
        )}
      </StyledFormControl>
    );
  };

  const renderInputField = (field: FormField) => {
    if (
      field.name === "villages_crossed_names" ||
      field.name === "tehsils_crossed_names"
    ) {
      const error =
        formik.touched[field.name as keyof typeof formik.touched] &&
        formik.errors[field.name as keyof typeof formik.errors];

      let maxItems: number | undefined;
      let disabled = false;

      if (field.name === "villages_crossed_names") {
        const count = formik.values.villages_crossed_count;

        if (!count || count == 0) {
          disabled = true; // ✅ disable if empty
        } else {
          maxItems = Number(count);
        }
      } else if (field.name === "tehsils_crossed_names") {
        const count = formik.values.tehsils_crossed_count;

        if (!count) {
          disabled = true; // ✅ disable if empty
        } else {
          maxItems = Number(count);
        }
      }

      return (
        <MultiStringInput
          label={field.label}
          name={field.name}
          value={formik.values[field.name] || []}
          onChange={(newValue) =>
            handleFieldChange(field.name, sanitizeTextOnly(newValue))
          }
          error={Boolean(error)}
          helperText={
            disabled
              ? "Please enter the count first"
              : error
                ? (formik.errors[field.name] as string)
                : undefined
          }
          maxItems={maxItems}
          disabled={disabled} // ✅ KEY LINE
        />
      );
    } else {
      const error =
        formik.touched[field.name as keyof typeof formik.touched] &&
        formik.errors[field.name as keyof typeof formik.errors];

      const inputProps: any = {
        readOnly: field.readOnly || false,

        // Disable arrow ↑ ↓, +, -, e
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (["ArrowUp", "ArrowDown", "+", "-"].includes(e.key)) {
            e.preventDefault();
          }
        },

        // Disable mouse wheel increment/decrement
        onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
          (e.target as HTMLInputElement).blur();
        },

        // Optional: mobile numeric keyboard
        inputMode: "numeric",
        pattern: "[0-9]*",
      };

      if (
        field.name === "villages_crossed_count" ||
        field.name === "tehsils_crossed_count"
      ) {
        inputProps.inputMode = "numeric";
        inputProps.pattern = "[0-9]*";

        inputProps.onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (["-", "+"].includes(e.key)) {
            e.preventDefault();
          }
        };

        inputProps.onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;

          // ✅ Allow empty value (important!)
          if (value === "") {
            return;
          }

          const num = Number(value);

          // ❌ Block non-numbers
          if (isNaN(num)) {
            e.target.value = "";
            return;
          }

          if (num > 50) {
            e.target.value = "50";
          }
        };
      }

      return (
        <StyledTextField
          sx={{
            width: "27rem",
            mb: 1,
            "& input[type=number]": {
              MozAppearance: "textfield",
            },
            "& input[type=number]::-webkit-outer-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
            "& input[type=number]::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
          }}
          id={field.name}
          name={field.name}
          type={field.inputType || "text"}
          value={formik.values[field.name as keyof typeof formik.values]}
          onChange={handleChange}
          onBlur={formik.handleBlur}
          fullWidth
          InputProps={inputProps}
          error={Boolean(error)}
          helperText={
            error
              ? (formik.errors[
                  field.name as keyof typeof formik.errors
                ] as string)
              : undefined
          }
          FormHelperTextProps={{
            sx: {
              fontSize: "12px !important",
              marginLeft: 0,
            },
          }}
        />
      );
    }
  };
  const renderField = (field: FormField) => {
    switch (field.type) {
      case "radio":
        return renderRadioField(field);
      case "select":
        return renderSelectField(field);
      case "input":
      default:
        return renderInputField(field);
    }
  };

  const renderChainageSection = () => (
    <Box>
      <Box
        display="flex"
        alignItems="flex-start"
        sx={{ width: "27rem", mb: 1 }}
      >
        {/* Timeline: Yellow + Dashed + Green */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          mr={2}
          className="chainage-timeline"
          position="relative"
        >
          {/* Yellow Dot */}
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#fbbf24",
              position: "relative",
              top: "6px",
              border: "1px solid #000000",
            }}
          />

          {/* Dashed Line */}
          <Box
            sx={{
              width: "2px",
              flexGrow: 1,
              background:
                "repeating-linear-gradient(transparent, transparent 4px, #ccc 4px, #ccc 8px)",
              marginY: "4px",
            }}
          />

          {/* Green Dot */}
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#22c55e",
              position: "relative",
              bottom: "6px",
              border: "1px solid #000000",
            }}
          />
        </Box>

        {/* Form Fields */}
        <Box>
          {/* Start Chainage */}
          <Typography fontWeight={600} fontSize="14px" mb={1}>
            Enter Start Chainage (KM / M)<span style={{ color: "red" }}>*</span>
          </Typography>
          <Box display="flex" gap={1} mb={2}>
            <Box>
              <InputLabel>Km</InputLabel>
              <StyledTextField
                name="start_chainage_km"
                value={formik.values.start_chainage_km}
                onChange={handleChange}
                onBlur={formik.handleBlur}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                error={
                  formik.touched.start_chainage_km &&
                  Boolean(formik.errors.start_chainage_km)
                }
                helperText={
                  formik.touched.start_chainage_km &&
                  formik.errors.start_chainage_km
                }
              />
            </Box>
            <span className="margin-plus">+</span>
            <Box>
              <InputLabel>m</InputLabel>
              <StyledTextField
                name="start_chainage_m"
                value={formik.values.start_chainage_m}
                onChange={handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.start_chainage_m &&
                  Boolean(formik.errors.start_chainage_m)
                }
                helperText={
                  formik.touched.start_chainage_m &&
                  formik.errors.start_chainage_m
                }
              />
            </Box>
          </Box>
          {/* End Chainage */}
          <Typography fontWeight={600} fontSize="14px" mb={1}>
            Enter End Chainage (KM / M)<span style={{ color: "red" }}>*</span>
          </Typography>
          <Box display="flex" gap={1} mb={2}>
            <Box>
              <InputLabel>Km</InputLabel>
              <StyledTextField
                name="end_chainage_km"
                value={formik.values.end_chainage_km}
                onChange={handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.end_chainage_km &&
                  Boolean(formik.errors.end_chainage_km)
                }
                helperText={
                  formik.touched.end_chainage_km &&
                  formik.errors.end_chainage_km
                }
              />
            </Box>
            <span className="margin-plus">+</span>
            <Box>
              <InputLabel>m</InputLabel>
              <StyledTextField
                name="end_chainage_m"
                value={formik.values.end_chainage_m}
                onChange={handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.end_chainage_m &&
                  Boolean(formik.errors.end_chainage_m)
                }
                helperText={
                  formik.touched.end_chainage_m && formik.errors.end_chainage_m
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
        }}
      >
        <Typography
          sx={{
            color: "#CC8801",
            fontSize: "14px",
            fontWeight: 600,
            mr: 0.5,
          }}
        >
          *
        </Typography>
        <Typography
          sx={{
            color: "#CC8801",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          Please Pin End Chainage location on the map to continue
        </Typography>
      </Box>
    </Box>
  );

  const renderUtilityAlignmentSection = () => {
    switch (selectedAlignment) {
      case "Along the Highway":
        return (
          <ChainageSection
            chainages={chainageDetails}
            setChainages={setChainageDetails}
            formik={formik}
            mode="chainage"
          />
        );

      case "Across the Highway":
        return (
          <ChainageSection
            chainages={crossingDetails}
            setChainages={setCrossingDetails}
            formik={formik}
            mode="crossing"
          />
        );

      case "Along & Across the Highway":
        return (
          <Box display="flex" flexDirection="column" gap={4}>
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                Along the Highway
              </Typography>

              <ChainageSection
                chainages={chainageDetails}
                setChainages={setChainageDetails}
                formik={formik}
                mode="chainage"
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                Across the Highway
              </Typography>

              <ChainageSection
                chainages={crossingDetails}
                setChainages={setCrossingDetails}
                formik={formik}
                mode="crossing"
              />
            </Box>
          </Box>
        );

      default:
        return (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            Please select Proposed Utility Alignment first.
          </Typography>
        );
    }
  };

  const renderLocationSearchSection = () => {
    return (
      <Accordion
        key="location-search"
        expanded={true}
        sx={{
          mb: 2,
          "&:before": { display: "none" },
          boxShadow: "none",
          borderRadius: "8px !important",
          // overflow: "hidden",
          transition: "all 0.3s ease",
          "&.Mui-expanded": {
            margin: 0,
            mb: 2,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: "#f5f5f5",
            borderBottom: "1px solid #e0e0e0",
            minHeight: "48px !important",
            "& .MuiAccordionSummary-content": {
              margin: "12px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: "16px", fontWeight: 600 }}
            display="flex"
            flexDirection="column"
          >
            <Typography component="span" sx={{ fontWeight: "bold" }}>
              <IconButton
                onClick={handleBackToFirstSection}
                sx={{ py: 0, my: 0 }}
              >
                <ArrowBackIcon />
              </IconButton>{" "}
              Search Your Utility Location
            </Typography>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: "16px 24px", overflow: "visible" }}>
          <Box
            className="amenities-wrapper flex flex-col justify-center items-center"
            sx={{ overflow: "visible" }}
          >
            <Box
              key="location_search"
              sx={{ width: "100%", position: "relative", zIndex: 1000 }}
            >
              <MapSearchInput />
              <Typography variant="h6" sx={{ mt: 1, color: "red" }}>
                Please search & click on highlighted highway stretch to locate
                your Ammenity/ Utility.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                  gap: 1,
                }}
              >
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToFirstSection}
                  variant="outlined"
                  sx={{ flex: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  sx={{ flex: 1 }}
                  onClick={handleSearchLocation}
                >
                  Search
                </Button>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSection = (section: Section, index: number) => {
    const isCompleted = completedSections.includes(index);
    const isEnabled = isSectionEnabled(index);
    const isExpanded = expandedSection === index;

    return (
      <>
        <Accordion
          key={section.title}
          expanded={isExpanded}
          onChange={handleAccordionChange(index)}
          disabled={!isEnabled}
          sx={{
            "&:before": { display: "none" },
            boxShadow: "none",
            // overflow: "hidden",
            transition: "all 0.3s ease",
            marginBottom: "0",
            "&.Mui-expanded": {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={isEnabled ? <ExpandMoreIcon /> : null}
            sx={{
              backgroundColor: "#FFF",
              borderBottom: "1px solid #e0e0e0",
              borderTop: "1px solid #e0e0e0",
              minHeight: "48px !important",
              "& .MuiAccordionSummary-content": {
                backgroundColor: "#FFF",
                margin: "12px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "16px", fontWeight: 600 }}
              display="flex"
              flexDirection="column"
            >
              <Typography component="span" sx={{ fontWeight: "bold" }}>
                {section.title}
              </Typography>
              {!isEnabled && (
                <Typography variant="caption">
                  (Complete previous sections first)
                </Typography>
              )}
            </Typography>
            {isCompleted && (
              <CheckCircleIcon sx={{ color: "success.main", ml: 1 }} />
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "16px 24px" }}>
            <Box className="amenities-wrapper flex flex-col justify-center">
              {section.title === "Chainage & Coordinates" ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#333",
                      mb: 3,
                      textAlign: "left",
                      lineHeight: 1.5,
                    }}
                  >
                    Chainage details are required for this submission. Please
                    click the button below to add your chainage details.
                  </Typography>

                  {renderUtilityAlignmentSection()}

                  <Typography
                    sx={{
                      mt: 3,
                      color: "#CC8801",
                      fontSize: "12px",
                      fontWeight: 600,
                      textAlign: "left",
                    }}
                  >
                    * Please pin the end location of the entire complete
                    chainage stretch on the map before proceeding
                  </Typography>
                </>
              ) : (
                section.fields
                  .filter((field) => {
                    if (field.name !== "other_utility_type") return true;

                    return (
                      String(formik.values.amenity).toLowerCase() ===
                      "others utilities"
                    );
                  })
                  .map((field: FormField) => (
                    <Box
                      key={field.name}
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Box sx={{ width: "27rem" }}>
                        <InputLabel
                          sx={{ mt: 1, mb: 0.3, textWrap: "wrap" }}
                          htmlFor={field.name}
                        >
                          {field.label}
                          {!field.optional && (
                            <span style={{ color: "red" }}>*</span>
                          )}
                        </InputLabel>
                        {renderField(field)}
                      </Box>
                    </Box>
                  ))
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
        {index === 0 && expandedSection === 0 && (
          <Box sx={{ textAlign: "center", pb: 2 }}>
            <Button
              sx={{
                background: "#3a1c73",
                padding: "8px 16px",
                color: "#fff",
                marginTop: "-0.5rem",
                "&.Mui-disabled": {
                  backgroundColor: "#9E9E9E",
                  color: "#fff",
                },
              }}
              onClick={() => setShowLocationSearch(true)}
              disabled={!isCompleted}
            >
              Search and Select on GIS Map
            </Button>
            <br />
            {!showMainSections && !showLocationSearch && (
              <>
                <Button
                  disabled
                  sx={{
                    width: "30rem",
                    marginTop: "8px",
                    background: "#FFF",
                    padding: "8px 16px",
                    color: "#3a1c73",
                    border: "1px solid #3a1c73",
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  Chainage & Coordinates
                </Button>
                <br />
                <Button
                  disabled
                  sx={{
                    width: "30rem",
                    marginTop: "8px",
                    background: "#FFF",
                    padding: "8px 16px",
                    color: "#3a1c73",
                    border: "1px solid #3a1c73",
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  Location Details
                </Button>
                <br />
                <Button
                  disabled
                  sx={{
                    width: "30rem",
                    marginTop: "8px",
                    background: "#FFF",
                    padding: "8px 16px",
                    color: "#3a1c73",
                    border: "1px solid #3a1c73",
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  Highway Details
                </Button>
              </>
            )}
          </Box>
        )}
      </>
    );
  };

  const renderFirstSectionOnly = () => (
    <Box>{renderSection(sections[0], 0)}</Box>
  );

  const renderMainSections = () => (
    <Box>{sections.map((section, index) => renderSection(section, index))}</Box>
  );

  const backButtonClickHandler = () => {
    navigate("/dashboard");
  };

  return (
    <>
      {/* BharatNet Undertaking Modal */}
      <Loader isLoading={isLoading} />
      <Dialog
        open={showBharatNetModal}
        onClose={handleBharatNetCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: "bold",
            backgroundColor: "#F4F2F7",
            borderBottom: "1px solid #E7E8E9",
            lineHeight: "normal",
          }}
        >
          BharatNet Project Declaration
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 3,
            pb: 2,
            "& .MuiTypography-root": {
              lineHeight: "1.8",
              color: "#333",
              textAlign: "justify",
            },
          }}
        >
          <Box sx={{ mt: 2, borderLeft: "2px solid #3a1c73", pl: 1 }}>
            <h4 className="gray-900 mb-2 font-medium text-justify">
              I hereby solemnly affirm and undertake that the instant
              application pertains exclusively to the BharatNet Project, and
              that no other utility, infrastructure, or work, other than that
              related to the BharatNet Project, is proposed or included therein.
            </h4>
            <h4 className="gray-900 mb-2 font-medium text-justify">
              I further undertake that, in the event it is subsequently found
              that any other application has been submitted, or is being
              forwarded, under the guise of or in connection with the BharatNet
              Project, the entire responsibility and liability in this regard
              shall rest solely with me, and I shall be fully accountable for
              the same.
            </h4>
            <h4 className="gray-900 font-medium text-justify">
              This undertaking is given in good faith and with full knowledge of
              its legal implications.
            </h4>
          </Box>
          {/* File Upload */}
          <Box sx={{ mt: 2 }}>
            <h4 className="gray-900 mb-1 font-bold">
              Upload Supporting Document <span style={{ color: "red" }}>*</span>
            </h4>
            <Box
              sx={{
                border: "2px dashed #c4b5e0",
                borderRadius: "8px",
                p: 2,
                textAlign: "center",
                backgroundColor: "#f9f7fd",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "#3a1c73",
                  backgroundColor: "#f0ecf9",
                },
              }}
              onClick={() =>
                document.getElementById("bharatnet-file-input")?.click()
              }
            >
              <input
                id="bharatnet-file-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: "none" }}
                // In the file upload onChange handler (around line 540-560), update the code:

                onChange={async (
                  event: React.ChangeEvent<HTMLInputElement>,
                ) => {
                  const fileInput = event.target;
                  const file = fileInput.files?.[0];

                  if (!file) return;

                  // ✅ FIX: Check file size FIRST before setting state
                  if (file.size > 20 * 1024 * 1024) {
                    toast.error(
                      "File size must be less than or equals to 20 MB",
                    ); // ✅ FIXED: Updated error message
                    setisLoading(false);
                    fileInput.value = ""; // Reset input
                    return; // ✅ IMPORTANT: Don't set the file in state
                  }

                  // Only set file in state AFTER validation passes
                  setBharatNetFile(file);
                  setisLoading(true);

                  // reset input so same file can be selected again
                  fileInput.value = "";

                  try {
                    // Step 1: Get signed URL
                    const fileUploadURL = await dispatch(
                      genericUploadThunk({
                        file,
                        moduleType: "row",
                      }),
                    );

                    if (!fileUploadURL?.payload?.success) {
                      const details =
                        fileUploadURL?.payload?.response?.data?.details ||
                        fileUploadURL?.payload?.details;

                      toast.error(
                        Array.isArray(details)
                          ? details[0]
                          : typeof details === "string"
                            ? details
                            : "Something Went Wrong!",
                      );

                      setBharatNetFile(null); // ✅ Clear file on error
                      setisLoading(false);
                      return;
                    }

                    setBharatnetRes(fileUploadURL?.payload?.data);

                    // Step 2: Upload to S3 (only in non-prod)
                    if (!productionCheck) {
                      const uploadSuccess = await dispatch(
                        uploadToS3Thunk({
                          signedUrl: fileUploadURL.payload.data.signedUrl,
                          file,
                          contentType: file.type,
                        }),
                      ).unwrap();

                      if (!uploadSuccess) {
                        toast.error("File upload to S3 failed");
                        setBharatNetFile(null); // ✅ Clear file on error
                        setisLoading(false);
                        return;
                      }
                    }

                    toast.success("File uploaded successfully!");
                  } catch (error) {
                    console.error(error);
                    toast.error("Upload failed");
                    setBharatNetFile(null); // ✅ Clear file on error
                  } finally {
                    setisLoading(false);
                  }
                }}
              />
              {bharatNetFile ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  {/* <PictureAsPdfOutlined sx={{ color: "error.main" }}/> */}
                  <Typography
                    variant="body2"
                    sx={{ color: "#3a1c73", fontWeight: 500 }}
                  >
                    {bharatNetFile.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBharatNetFile(null);
                      const input = document.getElementById(
                        "bharatnet-file-input",
                      ) as HTMLInputElement;
                      if (input) input.value = "";
                    }}
                    sx={{ color: "error.main" }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666", textAlign: "center !important" }}
                  >
                    Click to upload or drag and drop
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#999" }}>
                    PDF, JPG, JPEG, PNG (max 20MB)
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          {/* Consent Checkbox */}
          <Box
            sx={{
              mt: 2,
              py: 1,
              px: 1,
              bgcolor: "#f9f7fd",
              border: "1px solid #3a1c73",
              borderRadius: "8px",
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Checkbox
              checked={bharatNetConsent}
              onChange={(e) => setBharatNetConsent(e.target.checked)}
              sx={{
                p: 0,
                mt: 0.3,
                color: "#3a1c73",
                "&.Mui-checked": { color: "#3a1c73" },
              }}
            />
            <h4 className="gray-900 font-medium text-justify">
              I hereby confirm that I have read, understood, and agree to be
              legally bound by the above declaration.
            </h4>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #E7E8E9", p: 2 }}>
          <Button
            onClick={handleBharatNetCancel}
            variant="outlined"
            sx={{
              borderColor: "#3a1c73",
              color: "#3a1c73",
              borderRadius: "2rem",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          {/* Find the "Yes, I Agree" button (around line 720-730) and update: */}
          <Button
            onClick={handleBharatNetAcknowledge}
            variant="contained"
            disabled={
              !bharatNetConsent ||
              !bharatNetFile ||
              (bharatNetFile && bharatNetFile.size > 20 * 1024 * 1024) // ✅ Additional check
            }
            sx={{
              backgroundColor: "#3a1c73",
              color: "white",
              borderRadius: "2rem",
              textTransform: "none",
              "&.Mui-disabled": {
                backgroundColor: "#9E9E9E",
                color: "#fff",
              },
            }}
          >
            Yes, I Agree
          </Button>
        </DialogActions>
      </Dialog>

      <Box>
        <Box
          sx={{
            position: { xs: "static", md: "absolute" },
            zIndex: 20,
            top: { md: 8 },
            left: { md: 35 },
            width: { xs: "100%", sm: "100%", md: "394px" },
            maxWidth: "100vw",
            height: "fit-content",
            overflow: "visible",
            px: { xs: 1, sm: 2, md: 0 },
            className: "amenities-container",
          }}
        >
          <Box
            className="form-wrapper form-wrapper-row"
            sx={{
              maxHeight: { xs: "none", md: "29.5rem" },
              overflow: showLocationSearch ? "visible" : "hidden",
              overflowY: showLocationSearch ? "visible" : "auto",
              padding: "0px !important",
              transition: "overflow 0.3s ease",
              backgroundColor: "#fff",
            }}
          >
            <form onSubmit={formik.handleSubmit}>
              <Box pb={2}>
                {!showLocationSearch &&
                  !showMainSections &&
                  renderFirstSectionOnly()}
                {showLocationSearch && renderLocationSearchSection()}
                {showMainSections &&
                  !showLocationSearch &&
                  renderMainSections()}
              </Box>
            </form>
          </Box>
        </Box>
        <Box
          className="stepper-btns gray:br-t-500 mt-3"
          sx={{
            position: { xs: "static", md: "absolute" },
            width: "100%",
            left: "0px",
            bottom: "0px",
            zIndex: 15,
          }}
        >
          <StepsButton
            onBack={backButtonClickHandler}
            onNext={formik.handleSubmit}
            nextDisabled={
              completedSections.length !== sections.length ||
              ((utility_type as any) === "BharatNet Project" &&
                !bharatNetAcknowledged)
            }
          />
        </Box>
      </Box>
    </>
  );
};
